import { cn } from '@/lib/utils';
import { STATUS_SOLID_CLASS } from '@/styles/statusColors';
import { formatDisplayDateTime } from '@/utils/date';
import { PCMP_TIMELINE_STATUS_COLOR } from '../../constants/pcmpProposal.constants';
import type { PcmpProposalStudentClass, PcmpTimelineNode } from '../../types/pcmpProposal.types';
import { PcmpStatusPill } from './PcmpStatusPill';
import { PcmpTimelineEntryItem } from './PcmpTimelineEntryItem';

interface PcmpTimelineNodeCardProps {
  node: PcmpTimelineNode;
  students: PcmpProposalStudentClass[];
}

const STATUS_TEXT: Record<PcmpTimelineNode['status'], string> = {
  COMPLETED: 'Done',
  REJECTED: 'Rejected',
  ONHOLD: 'On hold',
  RELEASED: 'Released',
  EDITED: 'Edited',
  PENDING: 'Pending',
  WAITING: 'Waiting',
  OPTIONAL: 'Optional',
};

/** One approval step (legacy _TimelineNode). */
export function PcmpTimelineNodeCard({ node, students }: PcmpTimelineNodeCardProps) {
  const tone = PCMP_TIMELINE_STATUS_COLOR[node.status];
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-3 lg:w-56 lg:shrink-0',
        node.isCurrentUser && 'border-primary',
        node.isFallback && 'border-dashed border-warning-foreground',
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn('size-2.5 shrink-0 rounded-full', STATUS_SOLID_CLASS[tone])}
          aria-hidden
        />
        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {node.title}
        </span>
        {node.isCurrentUser ? <PcmpStatusPill tone="info">You</PcmpStatusPill> : null}
        {node.isInactiveUser ? <PcmpStatusPill tone="danger">Inactive</PcmpStatusPill> : null}
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-medium">{node.userName ?? 'Awaiting action'}</span>
        {node.designationName ? (
          <span className="text-xs text-muted-foreground">{node.designationName}</span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <PcmpStatusPill tone={tone}>{STATUS_TEXT[node.status]}</PcmpStatusPill>
        {node.subStatusLabel ? (
          <PcmpStatusPill tone="neutral">{node.subStatusLabel}</PcmpStatusPill>
        ) : null}
      </div>
      {node.occurredAt ? (
        <span className="text-xs text-muted-foreground">
          {formatDisplayDateTime(node.occurredAt)}
        </span>
      ) : null}
      {node.comment ? (
        <p className="text-xs whitespace-pre-line text-muted-foreground">“{node.comment}”</p>
      ) : null}
      {node.history.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {node.history.map((entry) => (
            <PcmpTimelineEntryItem key={entry.eventId} entry={entry} students={students} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
